import { StructuredMeasurements } from '@restDataAccess';
import { MeasurementRange } from '@scaleaq/sitecontrol-lib';
import { DateTime } from 'luxon';

import { WindowSize } from '@/dataAccess/rest/measurement/measurement.types.ts';
import { fromApiDateTime, round } from '@/utils';

export const getWindowSizeKey = (value: WindowSize): keyof typeof WindowSize => {
  const key = (Object.keys(WindowSize) as Array<keyof typeof WindowSize>).find(
    k => WindowSize[k] === value
  );
  if (!key) throw new Error(`Unknown WindowSize: ${value}`);
  return key;
};

/**
 * Gets the mean value from the latest measurement range in the provided array.
 * Picks by max timestamp (not array order). Returns undefined when the array is empty or missing.
 * Round matches the previous per-row `useLiceCount` display values.
 */
export const getLatestRangeMean = (
  measurementRanges: MeasurementRange[] | undefined
): number | undefined => {
  if (!measurementRanges?.length) return undefined;

  const latest = measurementRanges.reduce((newest, reading) =>
    fromApiDateTime(reading.timestamp).toMillis() > fromApiDateTime(newest.timestamp).toMillis()
      ? reading
      : newest
  );

  return round(latest.mean, 2);
};

/**
 * Days to pull the chart axis back from the exclusive query end.
 * `1` when nothing is timestamped on that open day, so an empty today is not drawn.
 * `0` when a reading is present, so a partial day stays on the axis and in the rows.
 */
export const cutIfNoData = ({
  measurements,
  cutoff,
}: {
  measurements: StructuredMeasurements<MeasurementRange>;
  cutoff: DateTime | undefined;
}): 0 | 1 => {
  if (!cutoff) return 0;

  const cutoffMillis = cutoff.toMillis();
  const hasOpenDayReading = Object.values(measurements).some(bySensor =>
    Object.values(bySensor).some(readings =>
      readings.some(reading => fromApiDateTime(reading.timestamp).toMillis() >= cutoffMillis)
    )
  );

  return hasOpenDayReading ? 0 : 1;
};
